import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser, logout, googleOAuthLogin, clearError } from "../../redux/slices/authSlice";
import { Link } from "react-router-dom";
import {
  IconArrowRight,
  IconBrandGoogle,
  IconBrandFacebook,
  IconBrandLinkedin,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandTwitter,
  IconX // Add this for close button
} from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import LoginImage from "../../assets/images/login.jpg";
import { NavLink } from "react-router-dom";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get both error and loading from Redux state
  const { error, loading } = useSelector((state) => state.auth);

  // Clear errors when component mounts or when user starts typing
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear errors when user starts typing
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      dispatch(clearError());
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) {
      dispatch(clearError());
    }
  };

  // Load Google Identity Services
  useEffect(() => {
    // Clean up any existing scripts first
    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.remove();
    }

    if (window.google) {
      setIsGoogleLoaded(true);
      initializeGoogleButton();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setTimeout(() => {
        if (window.google && window.google.accounts) {
          setIsGoogleLoaded(true);
          initializeGoogleButton();
        } else {
          setGoogleError('Failed to initialize Google services');
        }
      }, 500);
    };

    script.onerror = (error) => {
      setGoogleError('Failed to load Google services');
    };

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const initializeGoogleButton = () => {
    if (!window.google || !window.google.accounts) {
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: false,
      });

      const container = document.getElementById('google-button-container');
      if (container) {
        window.google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
        });
      }
    } catch (error) {
      setGoogleError('Failed to initialize Google button');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    dispatch(clearError()); // Clear any previous errors

    try {
      const resultAction = await dispatch(loginUser({ email, password }));

      if (loginUser.fulfilled.match(resultAction)) {
        const userRole = resultAction.payload.user_detail.role || resultAction.payload.user_detail.user_account_type;
        if (["super_admin", "manager", "editor"].includes(userRole)) {
          navigate("/admin");
        } else if (userRole === "customer") {
          navigate("/");
        } else {
          dispatch(logout());
        }
      }
      // If login fails, error will be handled by Redux rejected case
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleResponse = async (response) => {
    if (!response || !response.credential) {
      setGoogleLoading(false);
      setGoogleError('No credential received from Google');
      return;
    }

    setGoogleLoading(true);
    setGoogleError(null);

    try {
      const resultAction = await dispatch(googleOAuthLogin(response.credential));

      if (googleOAuthLogin.fulfilled.match(resultAction)) {
        setGoogleLoading(false);
        setGoogleError(null);

        const userRole = resultAction.payload.user_detail.role || resultAction.payload.user_detail.user_account_type;

        if (["super_admin", "manager", "editor"].includes(userRole)) {
          navigate("/admin");
        } else if (userRole === "customer") {
          navigate("/");
        } else {
          dispatch(logout());
        }
      } else {
        setGoogleLoading(false);
        setGoogleError(resultAction.payload?.message || 'Google login failed');
      }
    } catch (error) {
      setGoogleLoading(false);
      setGoogleError('Google authentication failed: ' + error.message);
    }
  };

  const handleDirectGoogleLogin = async () => {
    if (!window.google || !window.google.accounts) {
      setGoogleError('Google services not loaded');
      return;
    }

    setGoogleLoading(true);
    setGoogleError(null);

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: false,
      });

      const tempDiv = document.createElement('div');
      document.body.appendChild(tempDiv);

      window.google.accounts.id.renderButton(tempDiv, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: '300',
      });

      setTimeout(() => {
        const googleButton = tempDiv.querySelector('div[role="button"]');
        if (googleButton) {
          googleButton.click();
        } else {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              setGoogleLoading(false);
              setGoogleError('Google sign-in not available. Please try again.');
            }
          });
        }

        setTimeout(() => {
          if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv);
          }
        }, 1000);
      }, 500);
    } catch (error) {
      setGoogleLoading(false);
      setGoogleError('Failed to initialize Google login');
    }
  };

  // Helper function to get error message
  const getErrorMessage = (error) => {
    if (!error) return null;
    
    // Handle different error formats
    if (typeof error === 'string') {
      return error;
    }
    
    if (error.message) {
      return error.message;
    }
    
    if (error.detail) {
      return error.detail;
    }
    
    if (error.non_field_errors && Array.isArray(error.non_field_errors)) {
      return error.non_field_errors[0];
    }
    
    if (error.email && Array.isArray(error.email)) {
      return `Email: ${error.email[0]}`;
    }
    
    if (error.password && Array.isArray(error.password)) {
      return `Password: ${error.password[0]}`;
    }
    
    return 'Login failed. Please check your credentials and try again.';
  };

  const isAnyLoading = isLoading || googleLoading || loading;

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9FF]">
      <header className="h-16 bg-[#0B0C52] flex items-center justify-between px-4 md:px-8 text-white">
        <div className="flex flex-row items-center justify-center gap-3">
          <Link to="/" className="w-[50px] h-[50px] rounded-full bg-white overflow-hidden flex items-center justify-center">
            <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
          </Link>
          <span className="text-md font-slim tracking-wide">Paqtory</span>
        </div>
    
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-5xl w-full flex flex-col lg:flex-row items-center gap-8">
          <div className="flex justify-center lg:w-1/2">
            <img src={LoginImage} alt="Login" className="max-w-sm w-full h-auto object-cover rounded-md shadow" />
          </div>

          <div className="lg:w-1/2 flex justify-center">
            <form onSubmit={handleLogin} className="max-w-sm w-full bg-white rounded-md border border-[#FFD500]/30 p-6" style={{ boxShadow: "0 0 0 2px #FFD500" }}>
              <h2 className="text-2xl font-bold mb-6 text-[#0B0C52]">Sign in to Your Account</h2>

              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-700 text-sm">{getErrorMessage(error)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => dispatch(clearError())}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <IconX size={16} />
                  </button>
                </div>
              )}

              {/* Google Error Display */}
              {googleError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-700 text-sm">{googleError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGoogleError(null)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <IconX size={16} />
                  </button>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0C52] ${isAnyLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  required
                  disabled={isAnyLoading}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1">Password</label>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={handlePasswordChange}
                  className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0C52] ${isAnyLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  required
                  disabled={isAnyLoading}
                />
              </div>

              <div className="flex items-center justify-between mb-4 text-sm">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2 rounded focus:ring-0"
                    disabled={isAnyLoading}
                  />
                  Remember Me
                </label>
                <a
                  href="#forgot"
                  className={`text-[#0B0C52] hover:underline ${isAnyLoading ? "pointer-events-none opacity-50" : ""}`}
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                className="bg-[#FFD500] text-[#0B0C52] w-full py-2 rounded font-semibold flex items-center justify-center gap-2 hover:opacity-90 mb-4 disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isAnyLoading}
              >
                {isLoading || loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#0B0C52]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Logging in...
                  </span>
                ) : (
                  <>
                    Login <IconArrowRight size={20} />
                  </>
                )}
              </button>

              <div className="flex items-center justify-center mb-4">
                <div className="border-b border-gray-300 flex-1" />
                <span className="px-2 text-sm text-gray-500">Or</span>
                <div className="border-b border-gray-300 flex-1" />
              </div>

              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleDirectGoogleLogin}
                    className="w-full p-3 border border-[#0B0C52] rounded hover:bg-[#0B0C52] hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-white"
                    disabled={isAnyLoading || !isGoogleLoaded}
                  >
                    {googleLoading ? (
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <>
                        <IconBrandGoogle size={20} />
                        <span className="text-sm font-medium">Continue with Google</span>
                      </>
                    )}
                  </button>

                  <div id="google-button-container" style={{ position: 'absolute', left: '-9999px' }}></div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm mb-2">Don't Have An Account?</p>
                <button
                  type="button"
                  className="bg-[#0B0C52] text-white px-6 py-2 rounded text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isAnyLoading}
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <footer className="bg-[#0B0C52] border-t border-gray-200 py-6">
        <div className="max-w-5xl mx-auto px-4 flex flex-col items-center space-y-3 text-white">
          <div className="flex flex-wrap justify-center space-x-4 text-sm">
            <NavLink to="/" className="hover:underline">Home</NavLink>
            <span>|</span>
            <NavLink to="/product-categories" className="hover:underline">Categories</NavLink>
            <span>|</span>
            <NavLink to="/faq" className="hover:underline">FAQ</NavLink>
            <span>|</span>
            <NavLink to="/aboutus" className="hover:underline">About Us</NavLink>
            <span>|</span>
            <NavLink to="/contactus" className="hover:underline">Contact Us</NavLink>
          </div>
          <div className="flex space-x-3">
            {/* <SocialIcon href="#facebook">
              <IconBrandFacebook size={18} />
            </SocialIcon>
            <SocialIcon href="#twitter">
              <IconBrandTwitter size={18} />
            </SocialIcon>
            <SocialIcon href="#linkedin">
              <IconBrandLinkedin size={18} />
            </SocialIcon> */}
            <SocialIcon href="https://www.instagram.com/paqtory?igsh=MWQ5MTlsOHhicDg5cQ==">
              <IconBrandInstagram size={18} />
            </SocialIcon>
            {/* <SocialIcon href="#youtube">
              <IconBrandYoutube size={18} />
            </SocialIcon> */}
          </div>
          <p className="text-sm text-gray-400">© 2025 paqtory. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function SocialIcon({ href, children }) {
  return (
    <a
      href={href}
      className="w-8 h-8 bg-white border border-[#0B0C52] rounded flex items-center justify-center text-[#0B0C52] hover:bg-[#0B0C52] hover:text-white transition"
    >
      {children}
    </a>
  );
}